import { useRefUpdated } from 'ui/hooks/useRefUpdated'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type CountryCode,
  type PostNordCheckoutDeliveryOptionSelected,
  type PostNordCountry,
  type PostNordUpdateSessionRequestFE
} from 'ecosystem'
import { useForm, useWatch } from 'react-hook-form'
import { useDebouncedCallback, useDebounceValue } from 'ui/hooks'
import deepEqual from 'react-fast-compare'
import { useRadioGroup, useUpdateEffect } from '@chakra-ui/react'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { overrideText } from 'ui/lib/overrides'
import {
  type PostNordCheckoutEventDataChanged,
  type PostNordCheckoutEventDataChangedMeta,
  type PostNordCheckoutFormValues,
  type PostNordCheckoutProps
} from './types'
import { useRememberedAddress } from '../../hooks'
import {
  usePostnordGetDeliveryOptions,
  usePostnordUpdateSessionMutation,
  usePostnordValidatePostalCode
} from './apiCallHooks'
import { convertPostNordPatternsToRegExp } from './utils'
import { type PostNordOverrides } from './translations'

interface UsePostNordCheckoutProps extends Omit<PostNordCheckoutProps, 'containerProps'> {
  /** Countries the carrier form offers. Used to reject a remembered address we cannot ship to. */
  availableCountries?: CountryCode[]
}

export const usePostNordCheckout = (props?: UsePostNordCheckoutProps) => {
  const { onDataChange, cartTotalAmount, sessionId, initialData, orderItems, availableCountries } =
    props ?? {}
  const overrides = useOverridesContext<keyof PostNordOverrides>()

  const onDataChangeRef = useRefUpdated(onDataChange)

  const { remembered, remember, isOptedIn, setOptedIn } = useRememberedAddress('postnord')

  const [selectedDeliveryOption, setSelectedDeliveryOption] =
    useState<PostNordCheckoutDeliveryOptionSelected | null>(null)

  const selectedDeliveryOptionRef = useRef<PostNordCheckoutDeliveryOptionSelected | null>(
    selectedDeliveryOption
  )

  // Every field the form registers needs an entry here, `streetName` / `streetNumber`
  // included. `isDirty` is a diff against these defaults, and a field that registers
  // with `''` where the defaults say `undefined` counts as an edit — so omitting them
  // left the form permanently "dirty" from the moment `PostNordAddress` mounted, before
  // the shopper had touched anything. Anything gated on `isDirty` silently never ran.
  const form = useForm<PostNordCheckoutFormValues>({
    mode: 'onChange',
    defaultValues: {
      countryCode: (initialData?.recipientAddress?.countryCode ?? 'SE') as PostNordCountry,
      postCode: initialData?.recipientAddress?.postCode ?? '',
      city: initialData?.recipientAddress?.city ?? '',
      streetName: initialData?.recipientAddress?.streetName ?? '',
      streetNumber: initialData?.recipientAddress?.streetNumber ?? '',
      isPickupFromStore: initialData?.isPickupFromStore ?? false
    }
  })

  // Read during render: `formState` is a Proxy that only subscribes to the fields it
  // sees accessed in the render pass. Reading `isDirty` solely inside an effect leaves
  // it permanently false.
  const isFormDirty = form.formState.isDirty

  // Seed the recipient form once something to seed from arrives.
  //
  // Both sources land *after* `useForm` has taken its defaults — the order because
  // `orderResponse` is fetched, the device because the account has to settle first — so
  // neither can be a `defaultValues` entry. Seeding here covers both, and only while the
  // form is untouched: once the shopper types, what they typed wins.
  //
  // The order outranks the device and may arrive second, so it is allowed to overwrite a
  // device seed; the device is applied once and never again. Tracking which source won
  // rather than a single "done" flag is what stops a late order response from being
  // ignored — the bug that left this form blank when the shopper returned to a checkout
  // whose order already knew their address.
  const seededFromRef = useRef<'none' | 'device' | 'order'>('none')
  useEffect(() => {
    if (isFormDirty || seededFromRef.current === 'order') return

    const orderRecipient = initialData?.recipientAddress

    if (orderRecipient?.postCode) {
      seededFromRef.current = 'order'
      form.reset({
        ...form.getValues(),
        countryCode: (orderRecipient.countryCode || 'SE') as PostNordCountry,
        postCode: orderRecipient.postCode,
        city: orderRecipient.city ?? '',
        streetName: orderRecipient.streetName ?? '',
        streetNumber: orderRecipient.streetNumber ?? ''
      })
      return
    }

    if (seededFromRef.current === 'device' || !remembered) return
    // An address in a country this store does not ship to would sit in the form with the
    // country select showing nothing, and quote against a country PostNord will refuse.
    if (
      availableCountries?.length &&
      !availableCountries.includes(remembered.countryCode as CountryCode)
    )
      return

    seededFromRef.current = 'device'
    form.reset({
      ...form.getValues(),
      countryCode: (remembered.countryCode || 'SE') as PostNordCountry,
      postCode: remembered.postCode ?? '',
      city: remembered.city ?? '',
      streetName: remembered.streetName ?? '',
      streetNumber: remembered.streetNumber ?? ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dont put form
  }, [availableCountries, initialData, isFormDirty, remembered])

  const countryCode = useWatch({ control: form.control, name: 'countryCode' })
  const postCodeRaw = useWatch({ control: form.control, name: 'postCode' })
  const postCode = postCodeRaw?.replace(/\s/g, '') ?? ''
  const city = useWatch({ control: form.control, name: 'city' })
  const streetName = useWatch({ control: form.control, name: 'streetName' })
  const streetNumber = useWatch({ control: form.control, name: 'streetNumber' })
  const isPickupFromStore = useWatch({ control: form.control, name: 'isPickupFromStore' })
  const selectedPickup = useWatch({ control: form.control, name: 'selectedPickup' })

  const postCodeInvalid = form.getFieldState('postCode').invalid

  const { data: postalCodePatternResponse, isValidating: isLoadingPostalCodePattern } =
    usePostnordValidatePostalCode([{ countryCode }])

  // A home delivery is booked to a door, so it needs a street. An option that names a
  // pickup `location` is booked to that location and does not.
  const isRequiredStreetAddress = !selectedDeliveryOption?.details.location

  // Street address is required for home deliveries when we book shipment.
  //
  // Only nag once the shopper has actually engaged — picked a delivery option themselves,
  // or typed something. `selectedDeliveryOption` also lands on its own when the options
  // first load, and with a remembered postcode prefilling the form that now happens
  // before the shopper has touched anything: without this gate they open the checkout to
  // two red "Required" errors under fields they have never seen.
  const hasPickedOptionRef = useRef(false)
  useUpdateEffect(() => {
    if (!hasPickedOptionRef.current && !isFormDirty) return

    const postnordAddressFormElement = document.getElementById('postnord-address-form')
    const isFormInFocus = postnordAddressFormElement?.contains(document.activeElement)

    void form.trigger('streetNumber', { shouldFocus: !isFormInFocus && isRequiredStreetAddress })
    void form.trigger('streetName', { shouldFocus: !isFormInFocus && isRequiredStreetAddress })
  }, [selectedDeliveryOption])

  useEffect(() => {
    if (!postalCodePatternResponse?.[0]) {
      return undefined
    }

    if (form.getFieldState('postCode').isTouched) {
      void form.trigger('postCode')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- dont put form
  }, [postalCodePatternResponse])

  const postalCodePattern = useMemo(() => {
    if (!postalCodePatternResponse?.[0]) {
      return undefined
    }

    return convertPostNordPatternsToRegExp(postalCodePatternResponse[0].postalCodePattern)
  }, [postalCodePatternResponse])

  const [debouncedValidatePostalCodeParams] = useDebounceValue(
    {
      postCode,
      countryCode,
      postCodeInvalid
    },
    500,
    { equalityFn: deepEqual }
  )

  const { data: validatePostCodeResponse, isValidating: isLoadingValidationPostalCode } =
    usePostnordValidatePostalCode(
      [
        {
          postalCode: debouncedValidatePostalCodeParams.postCode,
          countryCode: debouncedValidatePostalCodeParams.countryCode
        }
      ],
      {
        skip:
          debouncedValidatePostalCodeParams.postCodeInvalid ||
          !debouncedValidatePostalCodeParams.postCode ||
          !debouncedValidatePostalCodeParams.countryCode
      }
    )

  const isCorrectPostalCode = useMemo(
    () => validatePostCodeResponse?.[0]?.validationResult === 'VALID',
    [validatePostCodeResponse]
  )

  const [debouncedGetDeliveryOptionsParams] = useDebounceValue(
    {
      postCode,
      countryCode,
      city,
      streetName,
      streetNumber,
      postCodeInvalid,
      isCorrectPostalCode
    },
    600,
    { equalityFn: deepEqual }
  )

  useEffect(() => {
    if (!validatePostCodeResponse) {
      return
    }

    const _isValidPostalCode = validatePostCodeResponse?.[0]?.validationResult === 'VALID'

    if (!_isValidPostalCode) {
      form.setError('postCode', {
        type: 'custom',
        message: overrideText('Ogiltigt postnummer', overrides?.errorInvalidPostalCode)
      })
      return
    }

    if (validatePostCodeResponse[0].city) {
      form.setValue('city', validatePostCodeResponse[0].city, { shouldValidate: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dont put form
  }, [validatePostCodeResponse])

  const { isLoading: isLoadingDeliveryOptions, data: deliveryOptionsData } =
    usePostnordGetDeliveryOptions(
      {
        cartTotalAmount,
        orderItems,
        recipient: {
          address: {
            countryCode: debouncedGetDeliveryOptionsParams.countryCode,
            postCode: debouncedGetDeliveryOptionsParams.postCode,
            city: debouncedGetDeliveryOptionsParams.city || undefined,
            streetName: debouncedGetDeliveryOptionsParams.streetName || undefined,
            streetNumber: debouncedGetDeliveryOptionsParams.streetNumber || undefined
          }
        }
      },
      {
        skip:
          isLoadingValidationPostalCode ||
          !debouncedGetDeliveryOptionsParams.isCorrectPostalCode ||
          debouncedGetDeliveryOptionsParams.postCodeInvalid ||
          !debouncedGetDeliveryOptionsParams.postCode ||
          !debouncedGetDeliveryOptionsParams.countryCode ||
          (!!debouncedGetDeliveryOptionsParams.streetNumber &&
            !debouncedGetDeliveryOptionsParams.streetName)
      }
    )

  const {
    getRootProps: getDeliveryOptionsRadioRootProps,
    getRadioProps: getDeliveryOptionsRadioGroupRadioProps,
    setValue: setDeliveryOptionsRadioValue
  } = useRadioGroup({
    name: 'delivery-option'
  })

  useUpdateEffect(() => {
    if (deliveryOptionsData) {
      const prevSelected = deliveryOptionsData.warehouseToDeliveryOptions[0].deliveryOptions.find(
        (option) => {
          return option.type === selectedDeliveryOptionRef.current?.type
        }
      )
      const firstDeliveryOption =
        deliveryOptionsData.warehouseToDeliveryOptions[0].deliveryOptions[0]

      const newSelected = prevSelected ?? firstDeliveryOption

      setDeliveryOptionsRadioValue(newSelected.id)
      setSelectedDeliveryOption({
        id: newSelected.id,
        type: newSelected.type,
        details: newSelected.additionalOptions?.length
          ? newSelected.additionalOptions[0]
          : newSelected.defaultOption!,
        price: newSelected.price
      })

      return
    }

    setDeliveryOptionsRadioValue('')
    setSelectedDeliveryOption(null)
  }, [deliveryOptionsData])

  const totalPrice = useMemo(() => {
    if (isPickupFromStore) {
      return 0
    }

    return selectedDeliveryOption?.price ?? 0
  }, [isPickupFromStore, selectedDeliveryOption])

  const [isUpdatingSession, setIsUpdatingSession] = useState(false)
  const [updatingSessionError, setUpdatingSessionError] = useState('')
  const retryTimeoutRef = useRef<number | undefined>(undefined)
  const { trigger: updateSessionMutation } = usePostnordUpdateSessionMutation()

  const updateSessionDebounce = useDebouncedCallback((payload: PostNordUpdateSessionRequestFE) => {
    window.clearTimeout(retryTimeoutRef.current)

    setIsUpdatingSession(true)
    setUpdatingSessionError('')

    const attemptUpdate = (attempt = 1) => {
      void updateSessionMutation(payload)
        .catch(() => {
          if (attempt < 5) {
            // Retry up to 5 times
            retryTimeoutRef.current = window.setTimeout(() => {
              attemptUpdate(attempt + 1)
            }, 1000 * attempt) // Exponential backoff: 1s, 2s, 3s
          } else {
            setUpdatingSessionError('Unknown error')
            setIsUpdatingSession(false)
          }
        })
        .then(() => {
          setIsUpdatingSession(false)
        })
    }

    attemptUpdate()
  }, 500)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      window.clearTimeout(retryTimeoutRef.current)
    }
  }, [])

  const isFormValid = form.formState.isValid

  // Checked explicitly rather than left to `isFormValid`.
  //
  // Whether the street is required changes with the delivery option, and react-hook-form
  // only recomputes `isValid` when a validation actually runs — so between picking a
  // home delivery and the next validation pass, `isValid` still reports the answer for
  // the *previous* option's rules. Payment gating hangs off this, and a widget unlocked
  // against a stale `true` lets an order through with no street to deliver to.
  const hasRequiredStreetAddress =
    !isRequiredStreetAddress || (!!streetName?.trim() && !!streetNumber?.trim())

  const partialCompleteness =
    isPickupFromStore ||
    (!!selectedDeliveryOption &&
      isFormValid &&
      hasRequiredStreetAddress &&
      isCorrectPostalCode &&
      !isLoadingValidationPostalCode &&
      !isLoadingDeliveryOptions)

  const completeness = !isUpdatingSession && !updatingSessionError && partialCompleteness

  useEffect(() => {
    if (!partialCompleteness || !sessionId) {
      return
    }

    updateSessionDebounce({
      sessionId,
      isPickupFromStore,
      selectedPickup,
      recipientAddress: {
        postCode,
        countryCode,
        city,
        streetName,
        streetNumber
      },
      deliveryOption: selectedDeliveryOption ?? undefined
    })
  }, [
    city,
    partialCompleteness,
    countryCode,
    isPickupFromStore,
    selectedPickup,
    postCode,
    selectedDeliveryOption,
    sessionId,
    streetName,
    streetNumber,
    updateSessionDebounce
  ])

  const dataRef = useRefUpdated<{
    data: PostNordCheckoutEventDataChanged
    meta: PostNordCheckoutEventDataChangedMeta
  }>({
    data: {
      isPickupFromStore,
      pickupFromStore: selectedPickup,
      deliveryOption: selectedDeliveryOption ?? undefined,
      totalPrice,
      recipientAddress: {
        postCode,
        countryCode,
        city,
        streetName,
        streetNumber
      }
    },
    meta: {
      isValid: completeness,
      deliveryOptionChanged: false,
      totalPriceChanged: false,
      recipientAddressChanged: false,
      isPickupFromStoreChanged: false,
      selectedPickupChanged: false
    }
  })

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      isValid: completeness
    })
  }, [completeness, dataRef, onDataChangeRef])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(
      {
        ...data,
        totalPrice
      },
      {
        ...meta,
        totalPriceChanged: true
      }
    )
  }, [dataRef, onDataChangeRef, totalPrice])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      isPickupFromStoreChanged: true
    })
  }, [dataRef, onDataChangeRef, isPickupFromStore])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      selectedPickupChanged: true
    })
  }, [dataRef, onDataChangeRef, selectedPickup])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      deliveryOptionChanged: true
    })
  }, [dataRef, onDataChangeRef, selectedDeliveryOption])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(
      {
        ...data,
        recipientAddress: {
          postCode,
          countryCode,
          city,
          streetName,
          streetNumber
        }
      },
      {
        ...meta,
        recipientAddressChanged: true
      }
    )
  }, [dataRef, onDataChangeRef, postCode, countryCode, city, streetName, streetNumber])

  // Remember the values that actually produced the quote on screen, not the live field
  // contents.
  //
  // This form has no Save button, so "valid" is the only commit signal available — but
  // valid is reached between keystrokes: the form passes its required-field rules as soon
  // as the street is one character long, and the delivery-options refetch that would
  // invalidate it is 600ms behind. Writing from the watched values therefore stores
  // `S`, `Sv`, `Sve`… and a shopper who abandons mid-word comes back to a truncated
  // street. These debounced values are the settled ones PostNord was actually asked
  // about, which makes them both the quieter and the more correct thing to keep.
  //
  // Pickup-from-store hides the recipient form entirely, so there is nothing to remember.
  useEffect(() => {
    const { postCode: settledPostCode, countryCode: settledCountryCode } =
      debouncedGetDeliveryOptionsParams

    if (!partialCompleteness || isPickupFromStore || !settledPostCode) return

    remember({
      postCode: settledPostCode,
      countryCode: settledCountryCode,
      city: debouncedGetDeliveryOptionsParams.city,
      streetName: debouncedGetDeliveryOptionsParams.streetName,
      streetNumber: debouncedGetDeliveryOptionsParams.streetNumber
    })
  }, [debouncedGetDeliveryOptionsParams, isPickupFromStore, partialCompleteness, remember])

  const onChangeDeliveryOption = useCallback((item: PostNordCheckoutDeliveryOptionSelected) => {
    hasPickedOptionRef.current = true
    setSelectedDeliveryOption(item)
    selectedDeliveryOptionRef.current = item
  }, [])

  return {
    form,
    selectedPickup,
    isPickupFromStore,
    isLoadingDeliveryOptions,
    deliveryOptionsData,
    onChangeDeliveryOption,
    isLoadingValidationPostalCode,
    postalCodePattern,
    isLoadingPostalCodePattern,
    getDeliveryOptionsRadioRootProps,
    getDeliveryOptionsRadioGroupRadioProps,
    isRequiredStreetAddress,
    isRememberOptedIn: isOptedIn,
    setRememberOptedIn: setOptedIn
  }
}
